/**
 * Rendering utilities for FDIModalManager
 */

export function escapeHTML(input) {
  if (input == null) return "";
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function valueOrDash(value) {
  const str = value == null || String(value).trim() === "" || value === "null" || value === "Null" ? "—" : String(value);
  return escapeHTML(str);
}

export function formatNumber(value) {
  if (value == null || value === "" || value === "null" || value === "Null") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return escapeHTML(String(value));
  return `$${num.toLocaleString()}`;
}

export function formatCountNumber(value) {
  if (value == null || value === "" || value === "null" || value === "Null") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return escapeHTML(String(value));
  return num.toLocaleString('vi-VN');
}

export function formatCapital(value) {
  if (value == null || value === "" || value === "null" || value === "Null") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return escapeHTML(String(value));
  
  /* Detect post type to determine currency formatting */
  const isDDI = document.body.classList.contains('post-type-archive-ddi');
  
  if (isDDI) {
    /* DDI uses VND formatting with Vietnamese abbreviations - no decimal places */
    if (num >= 1000000000) {
      const formatted = Math.floor(num / 1000000000);
      return `${formatted.toLocaleString('vi-VN')} tỷ VND`;
    } else if (num >= 1000000) {
      const formatted = Math.floor(num / 1000000);
      return `${formatted.toLocaleString('vi-VN')} triệu VND`;
    } else if (num >= 1000) {
      const formatted = Math.floor(num / 1000);
      return `${formatted.toLocaleString('vi-VN')} nghìn VND`;
    }
    return `${Math.floor(num).toLocaleString('vi-VN')} VND`;
  } else {
    /* FDI uses USD formatting */
    if (num >= 1000000000) {
      const formatted = (num / 1000000000).toFixed(2);
      return `$${parseFloat(formatted)}B`;
    } else if (num >= 1000000) {
      const formatted = (num / 1000000).toFixed(2);
      return `$${parseFloat(formatted)}M`;
    } else if (num >= 1000) {
      const formatted = (num / 1000).toFixed(2);
      return `$${parseFloat(formatted)}K`;
    }
    return `$${num.toLocaleString('vi-VN')}`;
  }
}

export function formatCapitalFull(value) {
  /* Bỏ qua nếu null, empty, hoặc <= 0 */
  if (value == null || 
      value === "" || 
      value === "null" || 
      value === "Null" || 
      Number(value) <= 0) {
    return null; /* conditionalRow sẽ bỏ qua */
  }
  
  const num = Number(value);
  if (Number.isNaN(num)) return escapeHTML(String(value));
  
  /* Detect post type to determine currency formatting */
  const isDDI = document.body.classList.contains('post-type-archive-ddi');
  
  if (isDDI) {
    /* DDI uses VND formatting - no decimal places */
    return `${Math.floor(num).toLocaleString('vi-VN')} VND`;
  } else {
    /* FDI uses USD formatting */
    return `$${num.toLocaleString('vi-VN')}`;
  }
}

export function formatLandArea(value) {
  if (value == null || 
      value === "" || 
      value === "null" || 
      value === "Null" || 
      Number(value) <= 0) {
    return null; /* conditionalRow sẽ bỏ qua */
  }
  
  const num = Number(value);
  if (Number.isNaN(num)) return escapeHTML(String(value));
  
  /* Chuyển đổi từ m² sang ha (1 ha = 10,000 m²) */
  const hectares = num / 10000;
  
  /* Format số với locale Việt Nam và thêm đơn vị ha */
  return `${hectares.toLocaleString('vi-VN')} ha`;
}

export function formatDate(value) {
  if (!value || value === "null" || value === "Null") return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return escapeHTML(String(value));
  return escapeHTML(d.toLocaleDateString("vi-VN"));
}

export function linkifyEmail(value) {
  if (!value || value === "null" || value === "Null") return "—";
  const safe = escapeHTML(value);
  return `<a class="text-blue-600 hover:underline" href="mailto:${safe}">${safe}</a>`;
}

export function linkifyPhone(value) {
  if (!value || value === "null" || value === "Null") return "—";
  const safe = escapeHTML(value);
  return `<a class="text-blue-600 hover:underline" href="tel:${safe}">${safe}</a>`;
}

/* Helper function to check if a value is empty or null */
export function isEmptyValue(value) {
  return value == null || 
         value === "" || 
         value === "null" || 
         value === "Null" || 
         String(value).trim() === "";
}

/* Helper function to check if any field in a section has data */
export function hasSectionData(fields) {
  return fields.some(field => !isEmptyValue(field));
}

export function sectionHeader(title) {
  return `
    <tr>
      <td colspan="2" class="pt-6 pb-2">
        <div class="text-xs uppercase tracking-wide text-gray-500">${escapeHTML(title)}</div>
      </td>
    </tr>
  `;
}

export function row(label, valueHtml) {
  return `
    <tr class="border-b last:border-0 border-gray-100 hover:bg-gray-50">
      <th scope="row" class="min-w-[33%] text-gray-500 font-medium align-top py-2 pr-4">${escapeHTML(label)}</th>
      <td class="w-2/3 text-gray-900 align-top py-2">${valueHtml}</td>
    </tr>
  `;
}

/* Conditional row - only renders if value is not empty */
export function conditionalRow(label, value, formatter = valueOrDash) {
  if (isEmptyValue(value)) return "";
  return row(label, formatter(value));
}

/* Conditional long text row - only renders if text is not empty */
export function conditionalLongTextRow(label, text) {
  if (isEmptyValue(text)) return "";
  return longTextRow(label, text);
}

export function longTextRow(label, text) {
  if (!text || text === "null" || text === "Null") return row(label, "—");
  const safe = escapeHTML(String(text));
  const needsToggle = safe.length > 100;
  if (!needsToggle) return row(label, `<div class=\"break-words\">${safe}</div>`);
  return row(
    label,
    `
      <div class="space-y-2">
        <div class="break-words line-clamp-3" data-fdi-collapsible>${safe}</div>
        <button type="button" class="text-xs text-blue-600 hover:underline" data-fdi-toggle>
          Xem thêm
        </button>
      </div>
    `,
  );
}

export function renderError(message, withRetry = false) {
  const safeMessage = escapeHTML(message || "An error occurred while loading details.");
  const retryBtn = withRetry
    ? '<button class="mt-3 px-3 py-2 bg-gray-800 text-white rounded text-sm" data-fdi-retry>Retry</button>'
    : "";
  return `
    <div class="p-6">
      <h3 class="text-base font-semibold mb-2">Unable to load details</h3>
      <p class="text-sm text-gray-600">${safeMessage}</p>
      ${retryBtn}
    </div>
  `;
}

export function renderDetail(data) {
  try {
    const title = data?.title?.rendered || data?.acf?.project_name || data?.title || "";
    const acf = data?.acf || {};

    const html = [];
    html.push(
      `<h3 class="text-base font-semibold !capitalize mb-3 break-words">${escapeHTML(title)}</h3>`,
    );

    html.push('<table class="w-full text-sm text-left border-separate" role="table">');
    html.push('<caption class="sr-only">FDI Project Details</caption>');
    html.push("<tbody>");

    /* Hành chính section */
    const adminFields = [acf?.project_code_initial, acf?.project_code, acf?.issue_date];
    if (hasSectionData(adminFields)) {
      html.push(sectionHeader("Hành chính"));
      html.push(conditionalRow("Mã số dự án lần đầu", acf?.project_code_initial));
      html.push(conditionalRow("Mã số dự án", acf?.project_code));
      html.push(conditionalRow("Ngày cấp", acf?.issue_date, formatDate));
    }

    /* Tổng quan section */
    const overviewFields = [acf?.company_name, acf?.project_name, acf?.managing_unit, acf?.sector_level1, acf?.section_level1, acf?.investment_form];
    if (hasSectionData(overviewFields)) {
      html.push(sectionHeader("Tổng quan"));
      html.push(conditionalRow("Tên doanh nghiệp", acf?.company_name));
      html.push(conditionalRow("Tên dự án", acf?.project_name));
      html.push(conditionalRow("Đơn vị quản lý", acf?.managing_unit));
      html.push(conditionalRow("Ngành cấp 1", acf?.sector_level1 || acf?.section_level1));
      html.push(conditionalRow("Hình thức đầu tư", acf?.investment_form));
    }

    /* Địa điểm section */
    const locationFields = [acf?.project_location, acf?.province, acf?.district];
    if (hasSectionData(locationFields)) {
      html.push(sectionHeader("Địa điểm"));
      html.push(conditionalLongTextRow("Địa điểm thực hiện dự án", acf?.project_location));
      html.push(conditionalRow("Tỉnh thành", acf?.province));
      html.push(conditionalRow("Quận huyện", acf?.district));
    }

    /* Nhà đầu tư nước ngoài section */
    const investorFields = [acf?.foreign_investor_name, acf?.investor_country, acf?.foreign_investor_email, acf?.foreign_investor_phone, acf?.foreign_investor_address];
    if (hasSectionData(investorFields)) {
      html.push(sectionHeader("Nhà đầu tư nước ngoài"));
      html.push(conditionalRow("Tên nhà đầu tư nước ngoài", acf?.foreign_investor_name));

      /* Special handling for country with flag */
      const countryValue = acf?.investor_country;
      if (!isEmptyValue(countryValue)) {
        let countryDisplay = valueOrDash(countryValue);
        if (data?.country_terms && Array.isArray(data.country_terms)) {
          const matchingCountry = data.country_terms.find(
            (term) => term.name === countryValue || term.slug === String(countryValue).toLowerCase(),
          );
          if (matchingCountry && matchingCountry.flag_url) {
            countryDisplay = `
              <div class="flex items-center gap-2">
                <img src="${escapeHTML(matchingCountry.flag_url)}" 
                     alt="Flag of ${escapeHTML(countryValue)}"
                     class="w-5 h-auto aspect-[3/2] object-cover border border-gray-200 rounded-sm"
                     loading="lazy">
                <span>${escapeHTML(countryValue)}</span>
              </div>
            `;
          }
        }
        html.push(row("Nước đầu tư", countryDisplay));
      }
      
      html.push(conditionalRow("Email nhà đầu tư nước ngoài", acf?.foreign_investor_email, linkifyEmail));
      html.push(conditionalRow("Điện thoại nhà đầu tư nước ngoài", acf?.foreign_investor_phone, linkifyPhone));
      html.push(conditionalRow("Địa chỉ nhà đầu tư nước ngoài", acf?.foreign_investor_address));
    }

    /* Người đại diện section */
    const representativeFields = [acf?.representative_name, acf?.representative_title, acf?.representative_nationality, acf?.representative_email, acf?.representative_phone, acf?.representative_permanent_address, acf?.representative_contact_address];
    if (hasSectionData(representativeFields)) {
      html.push(sectionHeader("Người đại diện"));
      html.push(conditionalRow("Họ tên người đại diện", acf?.representative_name));
      html.push(conditionalRow("Chức vụ người đại diện", acf?.representative_title));
      html.push(conditionalRow("Quốc tịch người đại diện", acf?.representative_nationality));
      html.push(conditionalRow("Email người đại diện", acf?.representative_email, linkifyEmail));
      html.push(conditionalRow("Điện thoại người đại diện", acf?.representative_phone, linkifyPhone));
      html.push(conditionalRow("Hộ khẩu thường trú", acf?.representative_permanent_address));
      html.push(conditionalRow("Địa chỉ người đại diện", acf?.representative_contact_address));
    }

    /* Tài chính section */
    const financialFields = [acf?.initial_registered_capital, acf?.registered_capital, acf?.contributed_capital, acf?.loan_capital, acf?.capital_contribution_schedule];
    if (hasSectionData(financialFields)) {
      html.push(sectionHeader("Tài chính"));
      html.push(conditionalRow("Vốn đăng ký ban đầu", acf?.initial_registered_capital, formatCapitalFull));
      html.push(conditionalRow("Vốn đăng ký", acf?.registered_capital, formatCapitalFull));
      html.push(conditionalRow("Vốn góp", acf?.contributed_capital, formatCapitalFull));
      html.push(conditionalRow("Vốn vay", acf?.loan_capital, formatCapitalFull));
      html.push(conditionalRow("Tiến độ góp vốn", acf?.capital_contribution_schedule));
    }

    /* Liên hệ doanh nghiệp section */
    const contactFields = [acf?.company_phone, acf?.company_fax, acf?.company_email, acf?.company_representative];
    if (hasSectionData(contactFields)) {
      html.push(sectionHeader("Liên hệ doanh nghiệp"));
      html.push(conditionalRow("Phone", acf?.company_phone, linkifyPhone));
      html.push(conditionalRow("Fax", acf?.company_fax));
      html.push(conditionalRow("Email", acf?.company_email, linkifyEmail));
      html.push(conditionalRow("Người đại diện doanh nghiệp", acf?.company_representative));
    }

    /* Mục tiêu & Quy mô section */
    const objectiveFields = [acf?.project_objective, acf?.project_scale];
    if (hasSectionData(objectiveFields)) {
      html.push(sectionHeader("Mục tiêu & Quy mô"));
      html.push(conditionalLongTextRow("Mục tiêu dự án", acf?.project_objective));
      html.push(conditionalLongTextRow("Quy mô", acf?.project_scale));
    }

    /* Hạ tầng section */
    const infrastructureFields = [acf?.land_area, acf?.factory_area];
    if (hasSectionData(infrastructureFields)) {
      html.push(sectionHeader("Hạ tầng"));
      html.push(conditionalRow("Diện tích đất", acf?.land_area, formatLandArea));
      html.push(conditionalRow("Diện tích nhà xưởng", acf?.factory_area));
    }

    /* Tiến độ section */
    const progressFields = [acf?.project_implementation_schedule];
    if (hasSectionData(progressFields)) {
      html.push(sectionHeader("Tiến độ"));
      html.push(conditionalLongTextRow("Tiến độ thực hiện", acf?.project_implementation_schedule));
    }

    html.push("</tbody>");
    html.push("</table>");

    return `<div class="p-6">${html.join("")}</div>`;
  } catch (e) {
    return renderError("Invalid data format");
  }
}
